import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useRiskFilters } from '../../shared/hooks/useRiskFilters'

export interface RiskProfileElementsSelectorProps {
  label: string
  placeholder: string
  /** Element-group buckets: [{ Transport: ['e1', 'e2'] }, { Buildings: ['e3'] }] */
  groups: Array<Record<string, string[]>>
  selected: string[]
  onChange: (ids: string[]) => void
  getLabel: (id: string) => string
  disabled?: boolean
}

type GroupCheckState = 'checked' | 'unchecked' | 'indeterminate'

function getGroupCheckState(memberIds: string[], selected: string[]): GroupCheckState {
  const selectedCount = memberIds.filter((id) => selected.includes(id)).length
  if (selectedCount === 0) return 'unchecked'
  if (selectedCount === memberIds.length) return 'checked'
  return 'indeterminate'
}

export function RiskProfileElementsSelector({
  label,
  placeholder,
  groups,
  selected,
  onChange,
  getLabel,
  disabled = false,
}: RiskProfileElementsSelectorProps) {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState<string[]>(selected)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(groups.map((g) => Object.keys(g)[0])),
  )

  const allElementIds = useMemo(() => groups.flatMap((g) => Object.values(g)[0]), [groups])
  const { filterToSingleElement } = useRiskFilters()

  useEffect(() => {
    if (open) setPending(selected)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const count = selected.length
  const total = allElementIds.length

  const triggerLabel =
    count === 0
      ? placeholder
      : count === total
        ? `All ${label.toLowerCase()}`
        : count === 1
          ? getLabel(selected[0])
          : `${count} selected`

  const toggleElement = useCallback((id: string) => {
    setPending((current) =>
      current.includes(id) ? current.filter((s) => s !== id) : [...current, id],
    )
  }, [])

  const toggleGroup = useCallback((memberIds: string[], state: GroupCheckState) => {
    setPending((current) => {
      if (state === 'checked') {
        // fully selected -> clear the whole group
        return current.filter((id) => !memberIds.includes(id))
      }
      // unchecked or indeterminate -> select every member
      const withoutGroup = current.filter((id) => !memberIds.includes(id))
      return [...withoutGroup, ...memberIds]
    })
  }, [])

  const toggleExpanded = (groupLabel: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current)
      if (next.has(groupLabel)) next.delete(groupLabel)
      else next.add(groupLabel)
      return next
    })
  }

  const clearAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setPending([])
  }

  const selectAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setPending(allElementIds)
  }
  const handleApply = () => {
    if (pending.length === 1) {
      filterToSingleElement(pending[0])
    } else {
      onChange(pending)
    }
    setOpen(false)
  }

  const pendingCount = pending.length
  const pendingTotal = allElementIds.length

  return (
    <div className="flex min-w-[220px] flex-1 flex-col gap-1">
      <label className="text-xs font-medium text-foreground">{label}</label>
      <Popover open={open} onOpenChange={(nextOpen) => !disabled && setOpen(nextOpen)}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="h-10 w-full justify-between rounded-lg border border-border bg-background px-3 text-sm font-normal shadow-sm hover:bg-background/80"
          >
            <span className="truncate text-foreground">{triggerLabel}</span>
            {open ? (
              <ChevronUp className="ml-2 h-4 w-4 shrink-0 text-foreground" />
            ) : (
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-foreground" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-full max-w-[420px] rounded-xl p-2"
        >
          <ScrollArea className="h-[280px] rounded-md pr-3">
            <div className="space-y-1 py-1 pr-2">
              {groups.map((group) => {
                const groupLabel = Object.keys(group)[0]
                const memberIds = Object.values(group)[0]
                const state = getGroupCheckState(memberIds, pending)
                const isExpanded = expandedGroups.has(groupLabel)

                return (
                  <div key={groupLabel}>
                    <div className="flex w-full items-center gap-2 rounded-md px-1 py-2 hover:bg-accent">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(groupLabel)}
                        className="flex h-6 w-6 shrink-0 items-center justify-center text-muted-foreground"
                        aria-label={isExpanded ? `Collapse ${groupLabel}` : `Expand ${groupLabel}`}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleGroup(memberIds, state)}
                        className="flex flex-1 items-center gap-3 text-left text-sm font-medium text-foreground"
                      >
                        <Checkbox
                          checked={
                            state === 'indeterminate' ? 'indeterminate' : state === 'checked'
                          }
                          aria-label={groupLabel}
                          className="pointer-events-none"
                        />

                        <span className="min-w-0 flex-1 truncate">{groupLabel}</span>
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="ml-4 border-l-2 border-border pl-5">
                        <div className="space-y-1 py-1">
                          {' '}
                          {memberIds.map((id) => {
                            const checked = pending.includes(id)
                            return (
                              <button
                                key={id}
                                type="button"
                                onClick={() => toggleElement(id)}
                                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
                              >
                                <Checkbox
                                  checked={checked}
                                  aria-label={getLabel(id)}
                                  className="pointer-events-none"
                                />
                                <span className="min-w-0 flex-1 truncate">{getLabel(id)}</span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <ScrollBar className="w-2 rounded-full bg-muted/60 p-0.5" />
          </ScrollArea>

          <div className="mt-2 flex items-center justify-between gap-x-2 border-t px-3 py-3">
            <span className="text-xs text-foreground">
              {pendingCount} of {pendingTotal} selected
            </span>
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={clearAll}
                className="font-semibold text-muted-foreground hover:text-foreground"
              >
                Clear all
              </Button>
              <Button variant="ghost" size="sm" type="button" onClick={selectAll}>
                Select all
              </Button>
            </div>
          </div>

          <Button onClick={handleApply} className="mt-2 w-full">
            Apply
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  )
}
